//TODO: This file is a temporary solution to map assignment validation messages to route segments. It should be removed once the backend provides a more structured way to handle validation errors.

const TRAILING_MESSAGE_PUNCTUATION_REGEX = /[\s.!?,;:…]+$/u;

/**
 * Normalizes BE validation texts for set-based routing:
 * trim + lowercase + collapse whitespace + ignore trailing punctuation.
 */
export const normalizeValidationMessage = (message?: string): string => {
	return (message ?? '')
		.trim()
		.toLowerCase()
		.replace(/\s+/g, ' ')
		.replace(TRAILING_MESSAGE_PUNCTUATION_REGEX, '')
		.trim();
};

const createMessageSet = (messages: readonly string[]): Set<string> => {
	return new Set(messages.map((message) => normalizeValidationMessage(message)));
};

export const DISABILITY_PROOF_UPLOAD_MESSAGES = createMessageSet([
	// Legacy messages kept as fallback.
	'bitte laden sie ein dokument am nachweis der behinderung hoch.',
	'bitte laden sie den nachweis der behinderung hoch.',

	// Current message.
	'bitte laden sie ein dokument als nachweis der behinderung hoch.',
]);

export const INSTITUTION_BRANCH_ADDRESS_MESSAGES = createMessageSet([
	// Legacy messages.
	'die plz oder der ort fehlt an der zweigstelle.',
	'die straße oder die hausnummer fehlt an der zweigstelle.',

	// Current messages.
	'der ort oder die straße fehlt an der zweigstelle.',
	'bitte geben sie die straße der hauptinstitution/zweigstelle an.',
	'bitte geben sie die plz und den ort der hauptinstitution/zweigstelle an.',

	// PGP4_01_30_31_06_08_01 / _04 / _05 / _06.
	'bitte geben sie die adresse der institution an.',
	'bitte vervollständigen sie die plz der institution.',
	'bitte vervollständigen sie den ort der institution.',
	'bitte vervollständigen sie die straße der institution.',
]);

export const CAREER_BEFORE_ASSIGNMENT_START_MESSAGES = createMessageSet([
	// Legacy message.
	'bitte beschäftigung vor dem beginn der beauftragung anlegen.',

	// Current message.
	'bitte legen sie den sozialrechtlichen status zeitlich vor den beginn der beauftragung.',
]);

export const CAREER_ROUTE_MESSAGES = createMessageSet([
	'bitte legen sie eine beschäftigung an.',
	'bitte wählen sie einen stadt/kreis für die beschäftigung.',
	'bitte wählen sie einen stadt/kreis für die tätigkeit.',
	'bitte wählen sie/sie einen stadt/kreis für die tätigkeit.',
	'bitte prüfen sie, ob die tätigkeit und ihr ansprechpartner in der gleichen institution.',
	'bitte verknüpfen sie eine tätigkeit mit der letzten beschäftigung.',
	'geben sie die aktuelle beschäftigung an.',
	'es gibt mehrere beschäftigungen mit dem gleichen datum.',
	'bitte stellen sie einen sozialrechtliche status ein, der eine schule ist.',
	"der zustand der letzten beschäftigung darf nicht 'voraussichtlich' sein.",
	'bitte legen sie einen sozialrechtlichen status im werdegang an.',
]);

export const ASSIGNMENT_DETAILS_ROUTE_MESSAGES = createMessageSet([
	// Legacy messages.
	'es sind keine berichte vorhanden.',
	'bitte erstellen sie einen anfangsbericht.',

	// RNDM_01_22.
	'bitte erstellen sie einen abschlussbericht.',
]);

export const ASSIGNMENT_EDIT_ROUTE_MESSAGES = createMessageSet([
	// PGP4_01_21_20.
	'es müssen erforderliche dokumente hochgeladen werden.',

	// PGP3_01_21_20 legacy variant.
	'bitte laden sie die erforderlichen dokumente für den auftrag hoch.',
	'bitte geben sie die betreuungsrelevante behinderungsart an.',
	'bitte wählen sie eine andere betreuungsrelevante behinderungsart. die aktuelle ist "genesen".',
	'die ausgewählte relevante behinderungsart ist genesen.',
]);

export const DISABILITIES_ROUTE_MESSAGES = createMessageSet([
	// Legacy messages.
	'bitte nachweis der behinderung vor dem beginn der beauftragung anlegen.',
	'der nachweis der behinderung muss vor dem kontaktbeginn liegen.',
	'eine ausdifferenzierung zur betreuungsrelevanten behinderung muss zur abmeldung angegeben werden.',

	// Current messages.
	'bitte geben sie eine ausdifferenzierung zur betreuungsrelevanten behinderung an.',
	'bitte markieren sie eine behinderungsart als betreuungsrelevant.',
	'die ausgewählte relevante behinderungsart ist gesundet.',
]);

export const CLIENT_EDIT_ROUTE_MESSAGES = createMessageSet([
	// Existing/current.
	'bitte geben sie den vornamen des klienten an.',
	'stadt/kreis (klient) fehlt (nur bei vm und üsb)',

	// Legacy messages.
	'bitte geben sie das geburtsdatum des klienten an bzw. überprüfen es.',
	'bitte vervollständigen sie das geschlecht des klienten.',
	'bitte vervollständigen sie die straße des klienten.',
	'bitte vervollständigen sie die plz des klienten.',

	// Current messages.
	'bitte geben sie das geburtsdatum des klienten an.',
	'bitte geben sie das geschlecht des klienten an.',
	'bitte geben sie die straße des klienten an.',
	'bitte geben sie die plz und den ort des klienten an.',
	'bitte geben sie die stadt / den kreis des wohnortes des klienten an.',
	'bitte vervollständigen sie den ort des klienten.',
	'bitte vervollständigen sie die adresse des klienten.',
	'bitte vervollständigen sie die staatsangehörigkeit des klienten.',
	'bitte vervollständigen sie das unbekannte geschlecht des klienten.',
]);

export const DISABILITY_TYPE_ADD_MESSAGES = createMessageSet([
	// Existing/current.
	'bitte legen sie (mindestens) eine behinderungsart an',
]);

export const DISABILITY_PROOF_ADD_MESSAGES = createMessageSet([
	// Current messages.
	'bitte legen sie einen nachweis der behinderung an.',
	'bitte erstellen sie einen nachweis der behinderung.',
]);

export const END_REPORT_EDITOR_MESSAGES = createMessageSet([
	// Additional assignment phase validation.
	'bitte vervollständigen sie den abschlussbericht.',
]);

/**
 * Core phrases of the end report "Sachstand" checks of the P4 phase validation
 * (PGP4_01_21_01 .. PGP4_01_21_09). Matched as substrings of the normalized message:
 * the BE texts contain quote characters and typos ("Sicherungs von ...") and their exact
 * wording is not stable, so the phrases deliberately stop before any quote character.
 */
export const END_REPORT_SACHSTAND_CORE_PHRASES: readonly string[] = [
	'sachstand aus problemfelder zum berichtszeitpunkt', // PGP4_01_21_01
	'sachstand aus problemfelder arbeitsbezogen', // PGP4_01_21_02
	'sachstand an (üsb', // PGP4_01_21_03
	'sachstand der hörbehinderung', // PGP4_01_21_04
	'sachstand aus "arbeits-',
	'sachstand aus weitere problemfelder', // PGP4_01_21_05
	'zustand des arbeitsverhältnisses zum beauftragungsende', // PGP4_01_21_06
	'sachstand im problemfeld', // PGP4_01_21_07 / _08 / _09
	'sachstand aus "sicherung von',
	'kein arbeitsverhältnis wegen', // Variante von PGP4_01_21_08 mit/ohne Anführungszeichen
	'bitte geben sie sachstände im bericht an',
];

/**
 * Whether a BE validation message is one of the end report "Sachstand" checks
 * (PGP4_01_21_01 .. _09). These checks are resolved in the end report editor.
 */
export const isEndReportSachstandMessage = (message?: string): boolean => {
	const normalized = normalizeValidationMessage(message);
	if (!normalized.includes('sachstand') && !normalized.includes('sachstände')) {
		return false;
	}

	return END_REPORT_SACHSTAND_CORE_PHRASES.some((phrase) => normalized.includes(phrase));
};

export const START_REPORT_EDITOR_MESSAGES = createMessageSet([
	// Existing/current.
	'bitte vervollständigen sie den anfangsbericht.',
	'bitte vervollständigen sie daten die in den anfangsbericht eingehen.',
]);

export const CLIENT_EDIT_FIELDS = new Set<string>(['dateofbirth', 'gender', 'firstname']);
